<template>
    <v-card>
        <v-card-title class="secondary elevation-4 white--text">
            <div class="text-h5">
                <v-icon class="pb-1" color="white" medium> check_circle </v-icon>
                {{ $t('Password_Update') }}
            </div>
        </v-card-title>
        <v-card-text class="pa-4">
            <div class="mb-4">
                <alert-warning>
                    {{ $t('Please_set_a_new_password') }}
                </alert-warning>
            </div>
            <v-form v-model="isValid" @submit.prevent>
                <text-field-label
                    v-if="userInfo"
                    :id="'old-password'"
                    v-model="updatePasswordUser.old_password"
                    :disabled="isProcessing"
                    :label="$t('Current_Password')"
                    :rules="[rules.required, rules.maxChars(updatePasswordUser.old_password, 256)]"
                    :type="isShowCurrentPassword ? 'text' : 'password'"
                    :append-icon="isShowCurrentPassword ? 'visibility_off' : 'visibility'"
                    :prepend-icon="'no_encryption'"
                    :name="'old-password'"
                    class="py-1"
                    @click:append="isShowCurrentPassword = !isShowCurrentPassword"
                />
                <text-field-label
                    :id="'new-password'"
                    v-model="updatePasswordUser.password"
                    :disabled="isProcessing"
                    :label="$t('New_Password')"
                    :rules="[rules.required, rules.maxChars(updatePasswordUser.password, 256)]"
                    :type="isShowNewPassword ? 'text' : 'password'"
                    :append-icon="isShowNewPassword ? 'visibility_off' : 'visibility'"
                    :prepend-icon="'lock'"
                    :name="'new-password'"
                    class="py-1"
                    @click:append="isShowNewPassword = !isShowNewPassword"
                />
                <text-field-label
                    :id="'confirm-password'"
                    v-model="confirmPassword"
                    :label="$t('Confirm_Password')"
                    :disabled="isProcessing"
                    :rules="[
                        rules.required,
                        rules.maxChars(confirmPassword, 256),
                        rules.samePassword(updatePasswordUser.password, confirmPassword),
                    ]"
                    :type="isShowConfirmPassword ? 'text' : 'password'"
                    :append-icon="isShowConfirmPassword ? 'visibility_off' : 'visibility'"
                    :prepend-icon="'check_circle_outline'"
                    :name="'confirm-password'"
                    class="py-1"
                    @keydown.13="updateType"
                    @click:append="isShowConfirmPassword = !isShowConfirmPassword"
                />
            </v-form>
        </v-card-text>
        <v-card-actions>
            <v-spacer />
            <button-app-control
                ref="update-btn"
                :label="$t('Update')"
                :icon="'refresh'"
                :disabled="!isValid"
                :loading="isProcessing"
                @click="updateType"
            />
        </v-card-actions>
    </v-card>
</template>

<script lang="ts">
import { mixins } from 'vue-class-component';
import { Vue, Component, Prop } from 'vue-property-decorator';
import { State, Action, Getter } from 'vuex-class';
import type { AuthState, User } from '../../../store/modules/auth/types';
import type { EditUser, UserState, UpdatePassword } from '../../../store/modules/user/types';
import InputValidation from '../../../mixins/InputValidation';
import AlertWarning from '../../atoms/AlertWarning.vue';
import { ButtonAppControl, TextFieldLabel } from '../../atoms';

@Component({
    name: 'TheChangePasswordForm',
    components: {
        AlertWarning,
        ButtonAppControl,
        TextFieldLabel,
    },
})
export default class TheChangePasswordForm extends mixins(InputValidation) {
    @State('user') user: UserState;
    @State('auth') auth: AuthState;
    @Action('updatePassword', { namespace: 'user' }) updatePassword;
    @Action('passwordErrorReset', { namespace: 'user' }) passwordErrorReset;
    @Action('updateForgottenPassword', { namespace: 'auth' }) updateForgottenPassword;
    @Action('clearAuthData', { namespace: 'auth' }) clearAuthData;
    @Action('overwriteAuthData', { namespace: 'auth' }) overwriteAuthData;
    @Getter('getAuthData', { namespace: 'auth' }) getAuthData: () => User;
    @Prop({ default: '' }) readonly token: string;

    updatePasswordUser = {
        old_password: '',
        password: '',
    };

    userInfo: User = {
        id: '',
        uid: '',
        name: '',
        authorities: [],
    };
    confirmPassword = '';
    isShowCurrentPassword = false;
    isShowNewPassword = false;
    isShowConfirmPassword = false;

    /**
     * lifecycle hooks
     */
    created() {
        this.passwordErrorReset();
        this.userInfo = this.getAuthData();
        this.clearAuthData();
    }

    /**
     * getter
     */
    get isProcessing(): boolean {
        return (
            this.user.update_password.isProcessing ||
            this.auth.updateForgottenPassword.isProcessing ||
            this.auth.processing
        );
    }
    get isDisableSubmit(): boolean {
        return !this.updatePasswordUser.password || !this.confirmPassword || this.isProcessing || !this.isValid;
    }

    /**
     * methods
     */
    updateType() {
        if (this.userInfo) {
            return this.updateNewPassword();
        } else {
            return this.forgottenPassword();
        }
    }
    async updateNewPassword() {
        if (!this.isDisableSubmit) {
            this.overwriteAuthData(this.userInfo);
            const commitUpdatePasswordUser: UpdatePassword = {
                token: this.token,
                id: parseInt(this.userInfo.id),
                old_password: this.updatePasswordUser.old_password,
                password: this.updatePasswordUser.password,
            };

            await this.updatePassword(commitUpdatePasswordUser);
        }
    }
    async forgottenPassword() {
        if (!this.isDisableSubmit) {
            await this.updateForgottenPassword({
                token: this.$route.query.token,
                password: this.updatePasswordUser.password,
            });
        }
    }
}
</script>
